import { useState } from "react";
import { trpc } from "../../lib/trpc.js";
import { Area, Botao, Campo, Chave, Entrada, Selo } from "../../components/ui.js";
import { SeletorModelo } from "./SeletorModelo.js";
import { SelecaoComOutro } from "./SelecaoComOutro.js";
import {
  MODALIDADE,
  OPCOES,
  dicaDe,
  ehBooleana,
  ehTextoLongo,
  estaLigada,
  rotuloDe,
  type ConfigDaLista,
} from "./secoes.js";

/**
 * Uma chave de configuração.
 *
 * O salvamento é por chave, não um botão global. O `upsert` do backend é por
 * chave; um "salvar tudo" precisaria orquestrar dezenove mutações e decidir o que
 * dizer quando três falham e as outras não.
 */
export function LinhaConfig({ config }: { config: ConfigDaLista }) {
  const utils = trpc.useUtils();
  const [valor, setValor] = useState(config.value);
  const [erro, setErro] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  const recarregar = () => {
    void utils.config.list.invalidate();
    void utils.config.integrationStatus.invalidate();
  };

  const salvar = trpc.config.upsert.useMutation({
    onSuccess: () => {
      setErro(null);
      setSalvo(true);
      // Segredo volta a ficar em branco, como veio do servidor: mantê-lo digitado
      // deixaria o botão ativo para sempre e o segredo na memória da aba sem
      // necessidade.
      if (config.isSecret) setValor("");
      recarregar();
    },
    onError: (e) => setErro(e.message),
  });

  const remover = trpc.config.delete.useMutation({
    onSuccess: recarregar,
    onError: (e) => setErro(e.message),
  });

  // Para segredo, o campo chega vazio e vazio significa "não alterar" — então só
  // há o que salvar quando algo foi digitado.
  const alterado = config.isSecret ? valor !== "" : valor !== config.value;
  const ocupado = salvar.isPending || remover.isPending;
  const modalidade = MODALIDADE[config.key];
  const booleana = ehBooleana(config);
  const opcoes = OPCOES[config.key];

  const aoDigitar = (v: string) => {
    setValor(v);
    setSalvo(false);
  };

  const gravar = (v: string) =>
    salvar.mutate({
      key: config.key,
      value: v,
      category: config.category,
      isSecret: config.isSecret,
    });

  return (
    <div className="rounded-peca border border-borda bg-superficie-alta p-4">
      <Campo rotulo={rotuloDe(config)} erro={erro} dica={dicaDe(config)}>
        {booleana ? (
          // Interruptor grava no toque. Exigir "Salvar" depois de alternar é pedir
          // dois gestos para uma decisão que já foi tomada no primeiro.
          <Chave
            ligada={estaLigada(config.key, valor)}
            aoAlternar={(l) => {
              const v = l ? "true" : "false";
              setValor(v);
              gravar(v);
            }}
            rotulo={rotuloDe(config)}
            disabled={ocupado}
          />
        ) : opcoes ? (
          <SelecaoComOutro
            opcoes={opcoes}
            valor={valor}
            onChange={aoDigitar}
            desabilitado={ocupado}
            rotulo={rotuloDe(config)}
          />
        ) : modalidade ? (
          <SeletorModelo
            modalidade={modalidade.modalidade}
            provedorDoCatalogo={modalidade.provedor}
            valor={valor}
            onChange={aoDigitar}
            desabilitado={ocupado}
          />
        ) : ehTextoLongo(config.key) ? (
          <Area
            value={valor}
            onChange={(e) => aoDigitar(e.target.value)}
            disabled={ocupado}
            spellCheck={false}
          />
        ) : (
          <Entrada
            type={config.isSecret ? "password" : "text"}
            value={valor}
            onChange={(e) => aoDigitar(e.target.value)}
            disabled={ocupado}
            autoComplete={config.isSecret ? "new-password" : "off"}
            placeholder={
              config.isSecret && config.hasValue
                ? "Configurado — deixe em branco para manter"
                : undefined
            }
          />
        )}
      </Campo>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        {!booleana && (
          <Botao
            tom="quieto"
            onClick={() => gravar(valor)}
            disabled={!alterado}
            carregando={salvar.isPending}
          >
            Salvar
          </Botao>
        )}

        {/* A chave técnica é o que aparece no banco e na documentação. Fica
            visível para quem precisa, discreta para quem não. */}
        <span className="font-mono text-[0.75rem] text-texto-fraco">{config.key}</span>

        {config.isSecret && <Selo tom="quieto">segredo</Selo>}
        {config.isSecret && config.hasValue && <Selo tom="fixado">configurado</Selo>}
        {config.isSecret && !config.hasValue && <Selo tom="descartar">em branco</Selo>}
        {salvo && !alterado && <span className="text-[0.8125rem] text-fixado">Salvo</span>}

        {/* Só o que o sistema não declara pode ser removido: apagar uma chave viva
            faria a integração dela cair sem aviso. */}
        {!config.conhecida && !confirmando && (
          <Botao tom="descartar" onClick={() => setConfirmando(true)} className="ml-auto">
            Remover
          </Botao>
        )}
      </div>

      {confirmando && (
        <div className="mt-3 flex flex-wrap items-center gap-3 rounded-peca border border-descartar/40 bg-descartar/10 px-4 py-3">
          <p className="flex-1 text-[0.875rem] leading-snug text-texto">
            Remover <span className="font-mono">{config.key}</span> em definitivo?
            {config.hasValue && config.isSecret && " O valor guardado será perdido."}
          </p>
          <Botao tom="quieto" onClick={() => setConfirmando(false)} disabled={remover.isPending}>
            Cancelar
          </Botao>
          <Botao
            tom="descartar"
            onClick={() => remover.mutate({ key: config.key })}
            carregando={remover.isPending}
          >
            Remover
          </Botao>
        </div>
      )}
    </div>
  );
}
